"use client"

import { useTranslations, useLocale } from 'next-intl'
import { useState, useEffect } from 'react'
import HeaderPage from './header-page'
import CircuitCard from '@/components/circuit-card'

export default function CircuitsClient() {
  const t = useTranslations()
  const locale = useLocale()
  const [circuits, setCircuits] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchCircuits()
  }, [])

  const fetchCircuits = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}voyages`)
      
      if (!response.ok) {
        throw new Error(`Failed to fetch circuits: ${response.status} ${response.statusText}`)
      }
      
      const data = await response.json()
      const circuitData = data.voyages
      setCircuits(circuitData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching circuits')
      console.error('Error fetching circuits:', err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      <HeaderPage
        title={t('navigation.circuits')}
        breadcrumbs={[
          { label: t('common.home'), href: "/" },
          { label: t('navigation.circuits') }
        ]}
      />

      <div className="p-4">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-red-600 font-medium">{t('circuits.error')}: {error}</p>
            <button
              onClick={fetchCircuits}
              className="mt-2 bg-red-100 text-red-700 px-4 py-2 rounded-lg hover:bg-red-200 transition"
            >
              {t('circuits.tryAgain')}
            </button>
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="flex flex-col bg-gray-200 shadow rounded-3xl overflow-hidden animate-pulse">
                <div className="h-48 w-full bg-gray-300"></div>
                <div className="p-5 space-y-3">
                  <div className="h-6 bg-gray-300 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-300 rounded w-full"></div>
                  <div className="h-4 bg-gray-300 rounded w-2/3"></div>
                  <div className="h-10 bg-gray-300 rounded-xl mt-2"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          circuits.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {circuits.map((circuit: any) => (
                <CircuitCard
                  key={circuit.id}
                  item={circuit}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-gray-50 rounded-2xl">
              <div className="text-gray-400 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-gray-500 text-lg font-medium mb-2">{t('circuits.noCircuitsAvailable')}</p>
              <p className="text-gray-400 mb-6">{t('circuits.checkBackLater')}</p>
              <button
                onClick={fetchCircuits}
                className="bg-primary text-white px-6 py-3 rounded-xl hover:bg-primary/80 transition font-medium"
              >
                {t('circuits.refresh')}
              </button>
            </div>
          )
        )}
      </div>
    </div>
  )
}
